/**
 * Incentives & Cards Page
 *
 * Consolidated view for:
 * - Overview (incentive summary, achievements)
 * - My Card (prepaid card visual and details)
 * - Payment History
 *
 * Reuses existing components: MemberIncentiveDashboard, MemberCardPortal, MemberPayoutHistory
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Gift,
  CreditCard,
  History,
  DollarSign,
  Star,
  Trophy,
  Target,
  TrendingUp,
  Loader2,
  Award,
  CheckCircle,
} from 'lucide-react';
import { getMemberData, getMemberToken } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import { useSubtabNavigation } from '@/hooks/useSubtabNavigation';
import EmployeePortalLayout from '../layout';
import MemberIncentiveDashboard from '@/components/incentives/MemberIncentiveDashboard';
import MemberCardPortal from '@/components/payments/MemberCardPortal';
import MemberPayoutHistory from '@/components/financial/MemberPayoutHistory';

interface IncentiveSummary {
  actions: {
    total: number;
    completed: number;
    total_points: number;
    potential_earnings: number;
  };
  earnings: {
    total_earnings: number;
    total_paid: number;
    pending_amount: number;
    total_points_earned: number;
  };
  recent_achievements: Array<{
    type: string;
    name: string;
    level: string | null;
    points: number;
    earned_date: string;
  }>;
}

interface MemberCard {
  id: number;
  card_number_last4: string;
  card_status: string;
  remaining_balance: number;
  spending_limit_amount: number;
  current_period_spending: number;
  expiration_month: string;
  expiration_year: string;
}

const tabs = [
  { id: 'overview', label: 'Overview', icon: Gift },
  { id: 'card', label: 'My Card', icon: CreditCard },
  { id: 'history', label: 'Payment History', icon: History },
];

export default function IncentivesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  useSubtabNavigation(setActiveTab, ['overview', 'card', 'history'] as const);
  const [loading, setLoading] = useState(true);
  const [memberId, setMemberId] = useState<number | null>(null);
  const [incentives, setIncentives] = useState<IncentiveSummary | null>(null);
  const [card, setCard] = useState<MemberCard | null>(null);

  const member = getMemberData();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!member?.id) {
        setLoading(false);
        return;
      }

      setMemberId(member.id);

      try {
        const token = getMemberToken();
        if (!token) {
          setLoading(false);
          return;
        }

        // Fetch incentive summary
        try {
          const incentivesUrl = getWeaveinApiUrl(`/api/incentives/members/${member.id}/summary`);
          const incentivesResponse = await fetch(incentivesUrl, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
              'Content-Type': 'application/json'
            }
          });

          if (incentivesResponse.ok) {
            setIncentives(await incentivesResponse.json());
          } else {
            // Mock data
            setIncentives({
              actions: {
                total: 12,
                completed: 8,
                total_points: 1500,
                potential_earnings: 150,
              },
              earnings: {
                total_earnings: 325.00,
                total_paid: 275.00,
                pending_amount: 50.00,
                total_points_earned: 1200,
              },
              recent_achievements: [
                { type: 'milestone', name: 'First Check-in', level: 'Gold', points: 100, earned_date: '2025-11-15' },
                { type: 'streak', name: '7-Day Streak', level: null, points: 75, earned_date: '2025-11-10' },
                { type: 'badge', name: 'Health Hero', level: 'Silver', points: 150, earned_date: '2025-11-01' },
              ],
            });
          }
        } catch (err) {
          console.error('Error fetching incentives:', err);
        }

        // Fetch card data
        try {
          const cardUrl = getWeaveinApiUrl(`/api/cards/member/${member.id}`);
          const cardResponse = await fetch(cardUrl, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
              'Content-Type': 'application/json'
            }
          });

          if (cardResponse.ok) {
            const cardData = await cardResponse.json();
            const activeCard = Array.isArray(cardData)
              ? cardData.find(c => c.card_status?.toLowerCase() === 'active') || cardData[0]
              : cardData;
            if (activeCard) {
              activeCard.remaining_balance = Number(activeCard.remaining_balance) || 0;
              activeCard.spending_limit_amount = Number(activeCard.spending_limit_amount) || 0;
              activeCard.current_period_spending = Number(activeCard.current_period_spending) || 0;
            }
            setCard(activeCard || null);
          } else {
            // Mock card
            setCard({
              id: 1,
              card_number_last4: '4521',
              card_status: 'ACTIVE',
              remaining_balance: 125.50,
              spending_limit_amount: 500,
              current_period_spending: 374.50,
              expiration_month: '12',
              expiration_year: '2027',
            });
          }
        } catch (err) {
          console.error('Error fetching card:', err);
        }
      } catch (error) {
        console.error('Error fetching incentives data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [member?.id]);

  if (loading) {
    return (
      <EmployeePortalLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </EmployeePortalLayout>
    );
  }

  return (
    <EmployeePortalLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Incentives & Cards</h1>
          <p className="text-gray-600 mt-1">
            Track your rewards, view your prepaid card, and check payment history.
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-sm border border-green-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-green-800">Total Earnings</span>
              <DollarSign className="w-4 h-4 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-900">
              ${incentives?.earnings.total_earnings.toFixed(2) || '0.00'}
            </p>
            <p className="text-xs text-green-700 mt-1">
              ${incentives?.earnings.pending_amount.toFixed(2) || '0.00'} pending
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-sm border border-purple-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-purple-800">Points Earned</span>
              <Star className="w-4 h-4 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-900">
              {incentives?.earnings.total_points_earned.toLocaleString() || 0}
            </p>
            <p className="text-xs text-purple-700 mt-1">
              {incentives?.recent_achievements.length || 0} achievements
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl shadow-sm border border-blue-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-800">Actions Progress</span>
              <Target className="w-4 h-4 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-900">
              {incentives?.actions.completed || 0}/{incentives?.actions.total || 0}
            </p>
            <div className="w-full bg-blue-200 rounded-full h-1.5 mt-2">
              <div
                className="bg-blue-600 h-1.5 rounded-full"
                style={{
                  width: `${incentives?.actions.total ? (incentives.actions.completed / incentives.actions.total) * 100 : 0}%`
                }}
              />
            </div>
          </div>

          {card && card.card_status?.toLowerCase() === 'active' && (
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-sm p-4 text-white">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-indigo-100">Card Balance</span>
                <CreditCard className="w-4 h-4 text-indigo-200" />
              </div>
              <p className="text-2xl font-bold">
                ${card.remaining_balance.toFixed(2)}
              </p>
              <p className="text-xs text-indigo-200 mt-1">
                •••• {card.card_number_last4}
              </p>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`
                      flex items-center px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap
                      ${isActive
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className={`w-4 h-4 mr-2 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && memberId && (
              <MemberIncentiveDashboard memberId={memberId} />
            )}

            {/* Card Tab */}
            {activeTab === 'card' && memberId && (
              <MemberCardPortal memberId={memberId} />
            )}

            {/* Payment History Tab */}
            {activeTab === 'history' && memberId && (
              <MemberPayoutHistory memberId={memberId} />
            )}
          </div>
        </div>
      </div>
    </EmployeePortalLayout>
  );
}
